import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContainer } from "@/components/auth/AuthContainer";
import { PasswordRecovery } from "@/components/auth/PasswordRecovery";
import { TokenRecovery } from "@/components/auth/TokenRecovery";
import { LoginForm } from "@/components/auth/login/LoginForm";

import { supabase } from "@/integrations/supabase/client";
import { cleanupAuthState } from "@/utils/auth-utils";
import { checkIsMonitor } from "@/hooks/useMonitorRedirect";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRecovery, setIsRecovery] = useState(false);
  const [isTokenRecovery, setIsTokenRecovery] = useState(false);
  const [view, setView] = useState<'sign_in' | 'sign_up'>('sign_in');

  // Helper function to redirect based on role
  const redirectBasedOnRole = async (userId: string) => {
    const isMonitor = await checkIsMonitor(userId);
    if (isMonitor) {
      console.log("🎯 Auth - user is monitor, redirecting to /monitor");
      navigate("/monitor", { replace: true });
    } else {
      console.log("🏠 Auth - user is not monitor, redirecting to /");
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    console.log("🔐 Auth - component mounted, cleaning up auth state");
    // Always clean up auth state when entering auth page to prevent stale tokens
    cleanupAuthState();
    
    // Check if there's a recovery token in URL params
    const token = searchParams.get('token');
    if (token) {
      console.log("🔑 Auth - recovery token found in URL, showing token recovery");
      setIsTokenRecovery(true);
      setIsRecovery(false);
      // Pre-fill the token in localStorage for the TokenForm
      localStorage.setItem('recovery_token_prefill', token);
    }
    
    const checkSession = async () => {
      try {
        console.log("🔍 Auth - checking for existing session");
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log("✅ Auth - valid session found, checking role before redirect");
          // Check if token is actually valid by making a test request
          const { error: userError } = await supabase.auth.getUser();
          
          if (!userError) {
            console.log("✅ Auth - session is valid, redirecting based on role");
            await redirectBasedOnRole(session.user.id);
          } else {
            // Invalid token, clean up
            console.log("❌ Auth - invalid session found, cleaning up");
            cleanupAuthState();
            await supabase.auth.signOut();
          }
        } else {
          console.log("ℹ️ Auth - no session found, staying on auth page");
        }
      } catch (error) {
        console.error("❌ Auth - error checking session:", error);
        cleanupAuthState();
      }
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("🔄 Auth - auth state changed:", event, !!session);
      
      if (event === 'SIGNED_IN' && session) {
        console.log("✅ Auth - user signed in, checking role before redirect");
        // Use setTimeout to avoid potential deadlocks
        setTimeout(async () => {
          await redirectBasedOnRole(session.user.id);
        }, 0);
      }
      
      if (event === 'TOKEN_REFRESHED' && session) {
        console.log("🔄 Auth - token refreshed, checking role before redirect");
        // Use setTimeout to avoid potential deadlocks
        setTimeout(async () => {
          await redirectBasedOnRole(session.user.id);
        }, 0);
      }
    });

    return () => {
      console.log("🧹 Auth - cleaning up subscription");
      subscription.unsubscribe();
    };
  }, [navigate, searchParams]);

  const handleBackToLogin = () => {
    setIsRecovery(false);
    setIsTokenRecovery(false);
    setView('sign_in');
  };

  const handleTokenRecovery = () => {
    setIsTokenRecovery(true);
    setIsRecovery(false);
  };

  const toggleView = () => {
    setView(view === 'sign_in' ? 'sign_up' : 'sign_in');
  };

  console.log("🎭 Auth - rendering auth page");

  return (
    <AuthContainer>
      {isRecovery ? (
        <PasswordRecovery 
          onBack={handleBackToLogin} 
          onTokenRecovery={handleTokenRecovery}
        />
      ) : isTokenRecovery ? (
        <TokenRecovery onBack={handleBackToLogin} />
      ) : (
        <>
          <LoginForm 
            view={view}
            onViewChange={setView}
            onRecoveryClick={() => setIsRecovery(true)}
          />
        </>
      )}
    </AuthContainer>
  );
};

export default Auth;
